import { useEffect, useMemo, useState } from "react";
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { Button } from "../../components/ui/button";
import aiBuddy from "../../assets/images/ai-buddy.jpg";
import courseBuilder from "../../assets/images/course-builder.jpg";
import learningAnalytics from "../../assets/images/learning-analytics.jpg";
import learningPath from "../../assets/images/learning-path.jpg";

// Used on the main site, and on a client subdomain whose Super Admin has not
// set up slides of their own.
const DEFAULT_SLIDES = [
  {
    eyebrow: "Learning AI",
    title: "Personalized Learning, Powered by AI",
    description:
      "Help every learner discover, understand and complete learning experiences tailored to their needs.",
    image: learningPath,
    alt: "Personalized AI learning path dashboard",
  },
  {
    eyebrow: "AI Learning Buddy",
    title: "Learn Smarter with Your AI Learning Buddy",
    description:
      "Get contextual guidance, recommendations and support throughout your learning journey.",
    image: aiBuddy,
    alt: "AI learning assistant conversation interface",
  },
  {
    eyebrow: "Authoring",
    title: "Build and Deliver Learning Experiences",
    description:
      "Create structured learning content and deliver it seamlessly through your learning platform.",
    image: courseBuilder,
    alt: "Course creation and learning content dashboard",
  },
  {
    eyebrow: "Analytics",
    title: "Turn Learning Into Measurable Growth",
    description:
      "Track progress, engagement and learning outcomes from one intelligent platform.",
    image: learningAnalytics,
    alt: "Learning progress and engagement analytics dashboard",
  },
];

// Org theme: useTenant() sets --org-primary / --org-accent on <html>.
// Fallbacks are the EthosBoard colours, so the main site looks the same as before.
const PRIMARY = 'var(--org-primary, #4F46E5)';
const ACCENT = 'var(--org-accent, #4338CA)';

const THEMED = {
  wash: {
    background: `linear-gradient(145deg, color-mix(in srgb, ${PRIMARY} 16%, white) 0%, color-mix(in srgb, ${ACCENT} 10%, white) 100%)`,
  },
  pill: {
    background: `color-mix(in srgb, ${PRIMARY} 14%, white)`,
    color: PRIMARY,
  },
  arrow: { color: PRIMARY },
  dotActive: { background: PRIMARY },
};

// brandName is only passed on a client subdomain.
// slides is the organization's own slide list (from useTenant().loginSlides):
//   [{ eyebrow, title, description, alt, image }]
// When it is empty or missing, the default EthosBoard slides are shown.
// A custom slide without an image falls back to the default image in the same position.
export function LoginCarousel({ brandName = null, slides: customSlides = null }) {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const themed = Boolean(brandName);
  const displayName = brandName || "EthosBoard";

  const slides = useMemo(() => {
    if (!Array.isArray(customSlides) || customSlides.length === 0) {
      return DEFAULT_SLIDES;
    }
    return customSlides.map((slide, index) => ({
      eyebrow: slide.eyebrow || "",
      title: slide.title || "",
      description: slide.description || "",
      image: slide.image || DEFAULT_SLIDES[index % DEFAULT_SLIDES.length].image,
      alt: slide.alt || slide.title || `${displayName} slide ${index + 1}`,
    }));
  }, [customSlides, displayName]);

  // If the slide list changes (e.g. tenant data arrives), start from the first slide
  useEffect(() => {
    setActiveSlide(0);
  }, [slides]);

  useEffect(() => {
    if (
      slides.length < 2 ||
      isPaused ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return undefined;
    }
    const timer = window.setInterval(
      () => setActiveSlide((current) => (current + 1) % slides.length),
      5500,
    );
    return () => window.clearInterval(timer);
  }, [isPaused, slides.length]);

  const selectPrevious = () =>
    setActiveSlide((current) => (current - 1 + slides.length) % slides.length);
  const selectNext = () => setActiveSlide((current) => (current + 1) % slides.length);

  const multiple = slides.length > 1;

  return (
    <section
      className="relative hidden min-h-screen w-[55%] items-center justify-center overflow-hidden px-8 py-10 md:flex lg:px-14"
      aria-label={`${displayName} capabilities`}
      aria-roledescription="carousel"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={() => setIsPaused(false)}
    >
      <div
        className={`absolute inset-0 ${themed ? "" : "bg-carousel"}`}
        style={themed ? THEMED.wash : undefined}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-2xl">
        <div className="mb-6 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4">
          <p className="min-w-0 truncate text-xs font-bold uppercase text-muted-foreground">
            Why teams choose {displayName}
          </p>
          <p className="shrink-0 text-xs font-semibold text-muted-foreground">
            {String(activeSlide + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
          </p>
        </div>

        <div className="relative min-h-[520px] overflow-hidden rounded-3xl border border-white/60 bg-white/70 shadow-glass backdrop-blur-xl">
          {slides.map((slide, index) => (
            <article
              key={`${index}-${slide.title}`}
              aria-hidden={index !== activeSlide}
              className={`absolute inset-0 flex flex-col transition-all duration-500 motion-reduce:transition-none ${
                index === activeSlide
                  ? "translate-x-0 opacity-100"
                  : index < activeSlide
                    ? "-translate-x-6 opacity-0"
                    : "translate-x-6 opacity-0"
              }`}
            >
              <div className="px-7 pb-5 pt-7 lg:px-9 lg:pt-9">
                {slide.eyebrow && (
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-bold uppercase ${
                      themed ? "" : "bg-primary-100 text-primary-600"
                    }`}
                    style={themed ? THEMED.pill : undefined}
                  >
                    {slide.eyebrow}
                  </span>
                )}
                {slide.title && (
                  <h2 className="mt-4 max-w-xl text-left font-display text-3xl font-bold leading-tight text-foreground">
                    {slide.title}
                  </h2>
                )}
                {slide.description && (
                  <p className="mt-3 max-w-xl text-left text-sm leading-6 text-muted-foreground">
                    {slide.description}
                  </p>
                )}
              </div>
              <div className="mt-auto p-3 pt-0">
                <img
                  src={slide.image}
                  alt={slide.alt}
                  width={1024}
                  height={768}
                  loading={index === 0 ? "eager" : "lazy"}
                  className="aspect-[16/9] w-full rounded-2xl object-cover"
                />
              </div>
            </article>
          ))}
        </div>

        {multiple && (
          <div className="mt-6 flex items-center justify-between">
            <div className="flex gap-2">
              <Button
                variant="carousel"
                size="icon"
                onClick={selectPrevious}
                aria-label="Previous slide"
                style={themed ? THEMED.arrow : undefined}
              >
                <FiChevronLeft />
              </Button>
              <Button
                variant="carousel"
                size="icon"
                onClick={selectNext}
                aria-label="Next slide"
                style={themed ? THEMED.arrow : undefined}
              >
                <FiChevronRight />
              </Button>
            </div>
            <div className="flex items-center gap-2" role="tablist" aria-label="Choose slide">
              {slides.map((slide, index) => {
                const active = index === activeSlide;
                return (
                  <button
                    key={`${index}-${slide.title}`}
                    type="button"
                    role="tab"
                    aria-label={`Show slide ${index + 1}${slide.title ? `: ${slide.title}` : ""}`}
                    aria-selected={active}
                    onClick={() => setActiveSlide(index)}
                    className={`h-2.5 rounded-full transition-all focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring ${
                      active ? `w-7 ${themed ? "" : "bg-primary"}` : "w-2.5 bg-gray-300"
                    }`}
                    style={active && themed ? THEMED.dotActive : undefined}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
